import type { ReactNode } from "react";
import { Badge } from "@trillion/ui/components/ui/badge";
import { Button } from "@trillion/ui/components/ui/button";
import {
  Card,
  CardAction,
  CardContent,
  CardHeader,
  CardTitle,
} from "@trillion/ui/components/ui/card";
import { Play } from "lucide-react";
import { formatInstant } from "@/components/jobs/recurring-columns";
import { jobLabel } from "@/components/runs/run-columns";
import type { RecurringJob } from "@/lib/jobs-types";

interface JobDefinitionCardProps {
  job: RecurringJob;
  /** The trigger POST is in flight (Run now disabled, Last state Pending). */
  isTriggering: boolean;
  /** Accepted, and the definition has not yet reported the new run (Last state Pending). */
  isPending: boolean;
  onRunNow: () => void;
}

function Field({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="flex flex-col gap-1">
      <dt className="text-xs text-muted-foreground">{label}</dt>
      <dd className="text-sm">{children}</dd>
    </div>
  );
}

/**
 * One recurring job's definition (sc-114201): what it runs, when, where, how its last run went, and Run now.
 * Presentational — the page owns the fetch and the trigger lifecycle. The Last state overlay is the grid's rule:
 * Pending from the click until the definition reports the new run.
 */
export function JobDefinitionCard({ job, isTriggering, isPending, onRunNow }: JobDefinitionCardProps) {
  return (
    <Card>
      <CardHeader>
        <CardTitle>Definition</CardTitle>
        <CardAction>
          <Button variant="outline" size="sm" onClick={onRunNow} disabled={isTriggering}>
            <Play className="size-4" />
            Run now
          </Button>
        </CardAction>
      </CardHeader>
      <CardContent>
        <dl className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <Field label="Job id">
            <span className="font-mono">{job.id}</span>
          </Field>
          <Field label="Job">{jobLabel(job.job)}</Field>
          <Field label="Schedule">
            <span className="flex flex-col gap-1">
              {job.schedule_description ? <span>{job.schedule_description}</span> : null}
              <code className="w-fit rounded bg-muted px-1.5 py-0.5 text-xs">{job.cron}</code>
            </span>
          </Field>
          <Field label="Time zone">{job.time_zone_id ?? "—"}</Field>
          <Field label="Queue">{job.queue || "default"}</Field>
          <Field label="Next run">{formatInstant(job.next_execution)}</Field>
          <Field label="Last run">{formatInstant(job.last_execution)}</Field>
          <Field label="Last state">
            {isTriggering || isPending ? (
              <Badge variant="outline">Pending</Badge>
            ) : job.last_job_state ? (
              <Badge variant="outline">{job.last_job_state}</Badge>
            ) : (
              "—"
            )}
          </Field>
        </dl>
      </CardContent>
    </Card>
  );
}
